import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BookCover } from "../components/BookCover";
import { BookSearch } from "../components/BookSearch";
import { t, useLang } from "../lib/i18n";

const GENRES = ["Fiction", "Non-Fiction", "Fantasy", "Sci-Fi", "Mystery", "Romance", "Biography", "History", "Self-Help", "Poetry", "Young Adult", "Children's"];
const AGES = ["All Ages", "12+", "16+", "18+"];

// Translate the well-known filter option values via i18n keys. Falls back to
// the raw option if no key is defined (e.g. custom genre names from the DB).
function translateOption(o) {
  const map = {
    "All Ages": "age.all_ages",
    "12+": "age.12_plus",
    "16+": "age.16_plus",
    "18+": "age.18_plus",
  };
  if (map[o]) return t(map[o]);
  const key = `genre.${o.toLowerCase().replace(/\s+/g, "_")}`;
  const val = t(key);
  return val === key ? o : val;
}

const EMPTY = {
  title: "", author: "", cover_url: "", genre: "Fiction",
  age_rating: "All Ages", year: "", pages: "", description: "",
  openlibrary_key: "",
  series_name: "", series_index: "",
};

export default function AddBookPage() {
  useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  // Distinct series names already in the reader's library — powers the
  // "pick from existing" dropdown.
  const [existingSeries, setExistingSeries] = useState([]);
  const [creatingNewSeries, setCreatingNewSeries] = useState(false);

  useEffect(() => {
    api.get("/me/library").then(({ data }) => {
      // Endpoint returns an array of {book, my_rating, my_text} — not wrapped.
      const items = Array.isArray(data) ? data : (data?.items || []);
      const set = new Set();
      for (const it of items) {
        const s = (it.book?.series_name || "").trim();
        if (s) set.add(s);
      }
      const list = Array.from(set).sort((a, b) => a.localeCompare(b));
      setExistingSeries(list);
    }).catch(() => setExistingSeries([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSearchSelect = (data) => {
    setForm({ ...EMPTY, ...data });
  };

  const resetForm = () => setForm(EMPTY);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.openlibrary_key) {
      toast.error(t("addbook.pick_first"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year, 10) : null,
        pages: form.pages ? parseInt(form.pages, 10) : null,
        series_name: form.series_name?.trim() || "",
        series_index: form.series_index === "" || form.series_index == null
          ? null
          : parseFloat(form.series_index),
      };
      const { data } = await api.post("/books", payload);
      toast.success(t("addbook.added_toast"));
      navigate(`/books/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const hasBook = !!form.openlibrary_key;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="add-book-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">{t("addbook.eyebrow_new")}</div>
      <h1 className="font-heading text-5xl md:text-6xl text-[#0A0A0A] mt-3 mb-3 leading-[0.98]">{t("addbook.title")}</h1>
      <p className="text-[#525252] mb-10 max-w-xl">{t("addbook.subtitle_ol")}</p>

      <div className="grid md:grid-cols-12 gap-10">
        <form onSubmit={onSubmit} className="md:col-span-7 space-y-6" data-testid="add-book-form">
          <Field label={t("addbook.search_label")}>
            <BookSearch onSelect={handleSearchSelect} autoFocus />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">
              {t("addbook.powered_small")}
            </div>
          </Field>

          {hasBook && (
            <div className="border border-[rgba(0,0,0,0.10)] bg-[#E8DFC1] p-5 fade-up" data-testid="selected-book">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{t("addbook.selected")}</div>
                  <div className="font-heading text-2xl text-[#0A0A0A] mt-1 leading-tight truncate">{form.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mt-1">
                    {form.author}{form.year ? ` · ${form.year}` : ""}{form.pages ? ` · ${form.pages} ${t("addbook.pages_suffix")}` : ""}
                  </div>
                </div>
                <button type="button" onClick={resetForm} className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A]" data-testid="reset-search">{t("addbook.change")}</button>
              </div>
            </div>
          )}

          <fieldset disabled={!hasBook} className="space-y-6 disabled:opacity-40">
            <Field label={t("addbook.genre")} required>
              <select value={form.genre} onChange={(e) => set("genre", e.target.value)} className={inputCls} data-testid="add-genre">
                {GENRES.map((g) => <option key={g} value={g}>{translateOption(g)}</option>)}
              </select>
            </Field>

            <Field label={t("addbook.age_rating")} required>
              <div className="grid grid-cols-4 gap-2" data-testid="add-age-group">
                {AGES.map((a) => (
                  <button
                    type="button" key={a}
                    onClick={() => set("age_rating", a)}
                    className={`py-2.5 rounded-sm border text-xs font-mono uppercase tracking-widest transition-colors ${form.age_rating === a ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-[#FAF5E6] text-[#525252] border-[rgba(0,0,0,0.12)] hover:border-[#0A0A0A]"}`}
                    data-testid={`age-${a}`}
                  >{translateOption(a)}</button>
                ))}
              </div>
              <input type="hidden" value={form.age_rating} data-testid="add-age" readOnly />
            </Field>

            <div className="grid grid-cols-[2fr_1fr] gap-3">
              <Field label={t("addbook.add_to_series")}>
                {creatingNewSeries ? (
                  <>
                    <input
                      type="text"
                      value={form.series_name}
                      onChange={(e) => set("series_name", e.target.value)}
                      placeholder="e.g. Harry Potter"
                      maxLength={200}
                      className={inputCls}
                      data-testid="add-series-name"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => { setCreatingNewSeries(false); set("series_name", ""); }}
                      className="mt-2 inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] hover:underline"
                      data-testid="add-series-cancel-new"
                    >
                      ← {t("addbook.back_to_list")}
                    </button>
                  </>
                ) : (
                  <select
                    value={form.series_name}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__NEW__") {
                        setCreatingNewSeries(true);
                        set("series_name", "");
                      } else {
                        set("series_name", v);
                      }
                    }}
                    className={inputCls}
                    data-testid="add-series-name"
                  >
                    <option value="">— {t("addbook.no_series")} —</option>
                    {existingSeries.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                    <option value="__NEW__">+ {t("addbook.add_new_series")}…</option>
                  </select>
                )}
              </Field>
              <Field label={t("addbook.book_number")}>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.series_index}
                  onChange={(e) => set("series_index", e.target.value)}
                  placeholder="e.g. 3"
                  className={inputCls}
                  data-testid="add-series-index"
                />
              </Field>
            </div>
          </fieldset>

          <button type="submit" disabled={submitting || !hasBook}
            className="bg-[#0A0A0A] hover:bg-[#262626] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono uppercase tracking-widest text-xs px-8 py-3.5 rounded-sm transition-colors"
            data-testid="add-submit-button">
            {submitting ? t("addbook.adding") : t("addbook.add_to_library")}
          </button>
        </form>

        <div className="md:col-span-5">
          <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-3">{t("addbook.preview")}</div>
          <div className="max-w-[260px] sticky top-24">
            {hasBook ? (
              <>
                <BookCover src={form.cover_url} title={form.title} author={form.author} />
                {form.description && (
                  <p className="mt-4 text-sm text-[#525252] leading-relaxed line-clamp-6">{form.description}</p>
                )}
              </>
            ) : (
              <div className="aspect-[2/3] border border-dashed border-[rgba(0,0,0,0.12)] flex items-center justify-center text-center p-6">
                <div>
                  <div className="font-heading text-xl text-[#A3A3A3] leading-tight">{t("addbook.your_selection")}</div>
                  <div className="font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3] mt-2">{t("addbook.will_appear")}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#FAF5E6] border border-[rgba(0,0,0,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black disabled:bg-[#E8DFC1] disabled:cursor-not-allowed";

function Field({ label, required, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{label}{required && " *"}</span>
      {children}
    </label>
  );
}
